import config from 'config';
import storage from 'helpers/storage';
import { logout } from 'actions/auth';
import getLocale from 'helpers/getLocale';
import getCountry from 'helpers/getCountry';
import { readAsUint8Array } from 'helpers/readFileList';
import objectProxy from './objectProxy';
const { backendUrl } = config;

// On localhost the backend's CORS allowlist excludes our origin, so the dev
// server proxies through /__api (see src/setupProxy.js). Production keeps
// hitting api.{jobalto,profitroll}.com directly — same-origin from the user's
// browser, so CORS doesn't apply.
const IS_LOCAL_DEV =
  typeof window !== 'undefined' &&
  (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
const RESOLVED_BASE = IS_LOCAL_DEV ? '/__api' : backendUrl;

export const API_URL = RESOLVED_BASE + (RESOLVED_BASE.charAt(RESOLVED_BASE.length - 1) !== '/' ? '/' : '');

// Request deduplication and rate limiting
const pendingRequests = new Map();
const requestCache = new Map();
const requestTimestamps = [];

// Wipe per-user caches on auth transitions so user B never sees user A's
// cached response after a logout/login on the same tab.
export function clearRequestCache() {
  requestCache.clear();
  pendingRequests.clear();
}
const MAX_CONCURRENT_REQUESTS = 10;
const MIN_REQUEST_INTERVAL = 100; // Minimum 100ms between requests
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes cache TTL for successful requests
const FAILED_REQUEST_CACHE_TTL = 30 * 1000; // 30 seconds cache for failed requests (prevent rapid retries)
const MAX_RETRIES = 1; // Reduced to 1 retry to limit requests
const BASE_RETRY_DELAY = 1000; // 1 second
const REQUEST_TIMEOUT = 30000; // 30 seconds timeout for requests

// Clean up old cache entries
setInterval(() => {
  const now = Date.now();
  for (const [key, value] of requestCache.entries()) {
    const cacheTTL = value.ttl || CACHE_TTL;
    if (now - value.timestamp > cacheTTL) {
      requestCache.delete(key);
    }
  }
}, 60000); // Clean up every minute

const getResponseBody = async (response = {}, options) => {
  const contentType = response.headers.get('content-type') || '';

  if (contentType.includes('application/json')) {
    if (options?.rawFile && response.status === 200) {
      const blob = await response.blob();
      return blob;
    }
    const json = await response.json();
    return objectProxy(json, { headers: response.headers });
  }

  if (contentType.includes('text/html')) {
    const text = await response.text();
    return text;
  }

  const blob = await response.blob();
  return blob;
};

// Rate limiting: ensure minimum interval between requests
async function throttleRequest() {
  const now = Date.now();
  // Remove timestamps older than 1 second
  while (requestTimestamps.length > 0 && now - requestTimestamps[0] > 1000) {
    requestTimestamps.shift();
  }
  
  // If we have too many recent requests, wait
  if (requestTimestamps.length >= MAX_CONCURRENT_REQUESTS) {
    const oldestRequest = requestTimestamps[0];
    const waitTime = MIN_REQUEST_INTERVAL - (now - oldestRequest);
    if (waitTime > 0) {
      await new Promise(resolve => setTimeout(resolve, waitTime));
    }
  }
  
  requestTimestamps.push(Date.now());
}

// Get cache key for request
function getCacheKey(url, method, body) {
  return `${method}:${url}:${body ? JSON.stringify(body) : ''}`;
}

async function createRequest(request, action, dispatch, payload, options, isIgnore) {
  if (!navigator.onLine) {
    const error = new Error('ConnectionFailed');
    dispatch({ type: `${action}_FAIL`, payload: error });
    throw error;
  }

  const { url, ...conf } = request;
  const { method } = request;

  // Check for pending duplicate request (deduplication)
  const requestKey = getCacheKey(url, method, request.body);
  if (pendingRequests.has(requestKey) && method === 'get') {
    // Return the pending promise for GET requests
    return pendingRequests.get(requestKey);
  }

  // Check cache for GET requests
  if (method === 'get' && !options?.skipCache) {
    const cached = requestCache.get(requestKey);
    if (cached) {
      const cacheTTL = cached.ttl || CACHE_TTL;
      if (Date.now() - cached.timestamp < cacheTTL) {
        // If cached result is an error, return null instead of retrying
        if (cached.error) {
          return null;
        }
        return cached.data;
      }
    }
  }

  // Rate limiting
  await throttleRequest();

  dispatch({ type: action, payload, body: request.body, url, method });

  // Auth flows via the httpOnly ptr_session cookie (see helpers/authToken.js).
  // Anonymous Firebase users cannot mint a session cookie (backend requires
  // email_verified), so we fall back to the legacy `token` header when the
  // caller explicitly provides one or when an idToken is present in storage
  // from the anonymous-user branch of services/firebase.js::subscribe.
  const legacyToken = options?.token || storage.getItem('token');
  const sessionId = sessionStorage.getItem('sessionId');
  const sessionToken = sessionStorage.getItem('sessionToken');
  const headers = {
    ...conf.headers,
    'X-API-Language': getLocale(),
    'X-API-Country': getCountry(),
  };
  if (sessionId) headers['X-API-SessionId'] = sessionId;
  if (sessionToken) headers['X-API-Session-Token'] = sessionToken;
  if (legacyToken) headers.token = legacyToken;
  if (options?.orderToken) headers['X-Order-Token'] = options.orderToken;
  // Registration-funnel tag. Set by the /pro landing CTA before navigating
  // to /sign-up; the backend reads it during first-time user creation to
  // auto-grant the `consultant` role for users coming in via that funnel.
  // No-op once the user row exists; safe to keep in storage.
  try {
    const source = sessionStorage.getItem('registrationSource');
    if (source) headers['X-Source'] = source;
  } catch (_) {
    // sessionStorage unavailable (SSR / privacy mode) — skip.
  }

  const requestData = {
    ...conf,
    credentials: 'include',
    headers
  };

  // Create request promise with timeout
  const makeRequest = async (retryCount = 0) => {
    let timeoutId = null;
    try {
      // Add timeout to prevent hanging requests
      const timeoutPromise = new Promise((_, reject) => {
        timeoutId = setTimeout(() => {
          reject(new Error('Request timeout'));
        }, REQUEST_TIMEOUT);
      });

      const fetchPromise = fetch(url, requestData)
        .catch((fetchError) => {
          // Clear timeout on fetch error
          if (timeoutId) {
            clearTimeout(timeoutId);
            timeoutId = null;
          }
          // Re-throw to be handled by outer catch
          throw fetchError;
        })
        .finally(() => {
          // Clear timeout if fetch completes
          if (timeoutId) {
            clearTimeout(timeoutId);
            timeoutId = null;
          }
        });

      const response = await Promise.race([
        fetchPromise,
        timeoutPromise
      ]);

      if (!navigator.onLine) {
        const error = new Error('ConnectionFailed');
        dispatch({ type: `${action}_FAIL`, payload: error });
        throw error;
      }

      const responseBody = await getResponseBody(response, options);

      // getResponseBody may return a string (text/html) or Blob (binary). Guard
      // before any property access so the failure mode is a clean error from
      // the status branches below rather than a TypeError on `in`/`.error`.
      const isObjectBody =
        responseBody && typeof responseBody === 'object' && !(responseBody instanceof Blob);

      const errors = isObjectBody
        ? [].concat(responseBody.error, responseBody.errors).filter(Boolean)
        : [];

      // Handle retryable errors with exponential backoff. 429 (rate limited)
      // gets a longer schedule than 5xx so the rate window has time to clear,
      // and we honour Retry-After if the server sends one.
      if (response.status >= 500 || response.status === 429) {
        const isRateLimited = response.status === 429;
        const maxRetries = isRateLimited ? 4 : MAX_RETRIES;
        if (retryCount < maxRetries) {
          let delay;
          if (isRateLimited) {
            const retryAfter = response.headers.get('retry-after');
            const headerDelay = retryAfter ? parseInt(retryAfter, 10) * 1000 : NaN;
            delay = Number.isFinite(headerDelay) && headerDelay > 0
              ? Math.min(headerDelay, 30000)
              : 2000 * Math.pow(2, retryCount); // 2s, 4s, 8s, 16s
          } else {
            delay = BASE_RETRY_DELAY * Math.pow(2, retryCount);
          }
          await new Promise(resolve => setTimeout(resolve, delay));
          return makeRequest(retryCount + 1);
        }
      }

      if (response.status === 401) {
        // Anonymous flows (e.g. assessment via voucher code) authenticate via
        // session-level tokens, not Firebase auth. A 401 there means the session
        // token is invalid for this endpoint, not that the user should be logged
        // out — `skipAuthLogout` opts the caller out of the global logout cascade.
        if (!options?.skipAuthLogout) {
          dispatch(logout());
        }
        throw new Error('401 unauthorized');
      }

      if (response.status === 403) {
        const error = new Error('403 forbidden');
        error.details =
          responseBody?.error?.details || responseBody?.error?.message || responseBody?.message;
        throw error;
      }

      if (response.status === 413) {
        throw new Error('413 Payload Too Large');
      }

      if (response.status === 404) {
        // Carry status like the errors.length branch below so callers can
        // reliably detect a 404 via error.status instead of message-matching.
        const error = new Error('404 not found');
        error.status = 404;
        throw error;
      }

      if (errors.length) {
        const errorMessage = errors.shift();
        const error = new Error(
          errorMessage?.error?.message || errorMessage?.message || errorMessage?.msg || errorMessage
        );
        error.status = response.status;
        error.details = errorMessage.details;
        error.response = responseBody;
        throw error;
      }

      const responseData =
        isObjectBody && 'data' in responseBody
          ? objectProxy(responseBody.data, { headers: response.headers })
          : responseBody;

      if (isObjectBody && (responseBody.meta || responseBody.pagination)) {
        responseData.meta = responseBody.pagination || responseBody.meta;
      }

      // Cache successful GET requests
      if (method === 'get' && !options?.skipCache) {
        requestCache.set(requestKey, {
          data: responseData,
          timestamp: Date.now()
        });
      }

      dispatch({
        type: `${action}_SUCCESS`,
        payload: responseData,
        request: payload,
        url,
        method,
        body: request.body
      });
      return responseData;
    } catch (error) {
      // Don't retry CORS errors, 502 errors, timeout errors, or "Failed to fetch" - they won't succeed on retry
      const errorMessage = error?.message || error?.toString() || '';
      const errorName = error?.name || '';
      
      // Detect various types of network/configuration errors that won't succeed on retry
      const isCorsError = errorMessage.includes('CORS') ||
        errorMessage.includes('Access-Control') ||
        errorMessage.includes('blocked by CORS policy');
      
      const isFailedToFetch = errorMessage === 'Failed to fetch' || 
        (errorName === 'TypeError' && errorMessage.includes('fetch'));
      
      const isBadGateway = errorMessage.includes('502') || errorMessage.includes('Bad Gateway');
      const isTimeout = errorMessage.includes('timeout') || errorMessage === 'Request timeout';
      
      // Don't retry errors that won't succeed (CORS, Failed to fetch, 502, timeout)
      if (isCorsError || isFailedToFetch || isBadGateway || isTimeout) {
        // Cache failed requests for a short time to prevent repeated attempts
        if (method === 'get' && !options?.skipCache) {
          requestCache.set(requestKey, {
            data: null,
            timestamp: Date.now(),
            error: true,
            ttl: FAILED_REQUEST_CACHE_TTL
          });
        }
      } else if (retryCount < MAX_RETRIES) {
        // Retry on genuine transient network errors with exponential backoff
        const isNetworkError = errorMessage === 'NetworkError' ||
          errorMessage === 'ConnectionFailed';
        
        if (isNetworkError) {
          const delay = BASE_RETRY_DELAY * Math.pow(2, retryCount);
          await new Promise(resolve => setTimeout(resolve, delay));
          return makeRequest(retryCount + 1);
        }
      }

      // Note: Removed showServiceMessage - it was too aggressive, showing a full-page
      // blocking error for any failed fetch. Individual pages handle errors via toast.

      dispatch({
        type: `${action}_FAIL`,
        payload: error,
        url,
        method,
        body: request.body,
        request: payload
      });

      // When the caller explicitly opts-in to suppress errors (e.g. public pages
      // that must render even if the backend is down), resolve with null instead
      // of rejecting to avoid unhandled promise rejections.
      if (options?.suppressErrors) {
        return null;
      }

      // Re-throw the error so it can be caught by callers
      throw error;
    } finally {
      // Ensure timeout is cleared
      if (timeoutId) clearTimeout(timeoutId);
    }
  };

  // Execute request and handle deduplication
  const requestPromise = makeRequest();
  
  // Store pending request for GET requests to enable deduplication
  // Attach error handler immediately to prevent unhandled rejections
  if (method === 'get') {
    // Cleanup is chained into the returned promise. A separate
    // handledPromise.finally() branch would be a dangling promise: on rejection
    // (404 / timeout / backend error) it rejects with no catch handler and fires
    // window.onunhandledrejection — even though every caller catches its own copy
    // of the returned promise.
    const handledPromise = requestPromise
      .catch((error) => {
        if (options?.suppressErrors) {
          return null;
        }
        throw error;
      })
      .finally(() => {
        pendingRequests.delete(requestKey);
      });

    pendingRequests.set(requestKey, handledPromise);

    return handledPromise;
  }

  // Ensure all promises have a catch handler to prevent unhandled rejections
  // This catch handler will be overridden by callers, but prevents unhandled rejections
  return requestPromise.catch((error) => {
    // This catch prevents unhandled rejections
    // The error will be re-thrown so callers can handle it
    throw error;
  });
}

export function get(url, action, dispatch, payload, options = {}) {
  const request = {
    url: API_URL + url,
    method: 'get'
  };

  const promise = createRequest(request, action, dispatch, payload, options);
  
  // Return the promise as-is - error handling is done in createRequest
  // Callers (like getLayout) will attach their own catch handlers
  return promise;
}

export function post(url, body, action, dispatch, additional = {}, options = {}, isIgnore) {
  const { headers, ...restOptions } = options;
  const request = {
    url: API_URL + url,
    method: 'post',
    headers: { 'Content-Type': 'application/json', ...(headers || {}) },
    body: JSON.stringify(body)
  };

  const promise = createRequest(request, action, dispatch, { ...body, ...additional }, restOptions, isIgnore);
  
  // Attach a catch handler immediately to prevent unhandled rejections
  const handledPromise = promise.catch((error) => {
    // Re-throw so callers can handle it, but having this catch prevents unhandled rejection
    throw error;
  });
  
  return handledPromise;
}

export function put(url, body, action, dispatch, additional = {}, options = {}) {
  const { headers } = options;

  const request = {
    url: API_URL + url,
    method: 'put',
    headers: { 'Content-Type': 'application/json', ...(headers || {}) },
    body: JSON.stringify(body)
  };

  return createRequest(request, action, dispatch, { ...body, ...additional });
}

export function patch(url, body, action, dispatch, additional = {}, options = {}) {
  const { headers } = options;

  const request = {
    url: API_URL + url,
    method: 'patch',
    headers: { 'Content-Type': 'application/json', ...(headers || {}) },
    body: JSON.stringify(body)
  };

  return createRequest(request, action, dispatch, { ...body, ...additional });
}

export function del(url, body, action, dispatch, payload) {
  const request = {
    url: API_URL + url,
    method: 'delete',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body)
  };

  return createRequest(request, action, dispatch, payload);
}

export async function upload(url, file, action, dispatch) {
  const binary = await readAsUint8Array(file);

  const request = {
    url: API_URL + url,
    method: 'put',
    headers: { 'Content-Type': file.type },
    body: binary
  };

  return createRequest(request, action, dispatch, binary);
}
